<template>
  <div>
    <div class="font-weight-bold">{{ location.name }}</div>

    <!-- Observation resultType -->
    <div v-if="resultType === 'observation'">
      <div>Observations: {{ location.observationsTotal }}</div>
    </div>

    <!-- Numeric resultType -->
    <div v-else-if="resultType === 'number'">
      <div>Observations: {{ location.observationsTotal }}</div>
      <div v-if="getLocationResult">
        <div class="font-weight-bold mt-2">
          {{ resultLabel }}
        </div>
        <div class="small">
          <div
            :class="{
              'font-weight-bold text-primary': aggregationNumeric === 'total',
            }"
          >
            <span class="text-muted">Total:</span>
            <span class="ml-1">{{ formatValue(getLocationResult.total) }}</span>
          </div>
          <div
            :class="{
              'font-weight-bold text-primary': aggregationNumeric === 'avg',
            }"
          >
            <span class="text-muted">Average:</span>
            <span class="ml-1">{{ formatValue(getLocationResult.avg) }}</span>
          </div>
          <div
            :class="{
              'font-weight-bold text-primary': aggregationNumeric === 'median',
            }"
          >
            <span class="text-muted">Median:</span>
            <span class="ml-1">{{
              formatValue(getLocationResult.median)
            }}</span>
          </div>
          <div
            :class="{
              'font-weight-bold text-primary':
                aggregationNumeric === 'most_recent',
            }"
          >
            <span class="text-muted">Most Recent:</span>
            <span class="ml-1">{{
              formatValue(getLocationResult.most_recent)
            }}</span>
          </div>
          <div class="mt-1">
            <span class="text-muted">Observations with data:</span>
            <span class="ml-1">{{ getLocationResult.observationsTotal }}</span>
          </div>
        </div>
      </div>
      <div v-else>
        <div class="small text-muted mt-2">
          No observations include {{ resultLabel }}
        </div>
      </div>
    </div>

    <!-- Dropdown/Radio resultType -->
    <div v-else-if="resultType === 'dropdown' || resultType === 'radio'">
      <div>Observations: {{ location.observationsTotal }}</div>
      <div v-if="getLocationResult">
        <div class="font-weight-bold mt-2">
          {{ resultLabel }}
        </div>
        <div class="small">
          <div
            :class="{
              'font-weight-bold text-primary':
                aggregationCategorical === 'most_recent',
            }"
          >
            <span class="text-muted">Most Recent:</span>
            <span class="ml-1">{{
              getLocationResult.most_recent || "N/A"
            }}</span>
          </div>
          <div
            :class="{
              'font-weight-bold text-primary':
                aggregationCategorical === 'most_frequent',
            }"
          >
            <span class="text-muted">Most Frequent":</span>
            <span class="ml-1">{{
              getLocationResult.most_frequent || "N/A"
            }}</span>
          </div>
          <div class="mt-1">
            <span class="text-muted">Observations with data:</span>
            <span class="ml-1">{{ getLocationResult.observationsTotal }}</span>
          </div>
        </div>
      </div>
      <div v-else>
        <div class="small text-muted mt-2">
          No observations include {{ resultLabel }}
        </div>
      </div>
    </div>

    <!-- Datasheet resultType -->
    <div v-else-if="resultType === 'datasheet'">
      <div>
        <div>
          <b
            ><span v-if="getLocationResult">{{
              getLocationResult.observationsTotal
            }}</span
            ><span v-else>0</span></b
          >
          of {{ location.observationsTotal }} observations submitted data for
          "{{ resultLabel }}".
        </div>
      </div>
      <div class="mt-2" v-if="showLegacyDataWarning">
        Some data from prior to CitSci 2.0 may not be visible on the map, but is
        visible by viewing
        <b-link :to="{ name: 'LocationDetails', params: { id: location.id } }"
          >this location's observations.</b-link
        >
      </div>
    </div>

    <!-- Datasheet Usage resultType -->
    <div v-else-if="resultType === 'datasheet_usage'">
      <div>Observations: {{ location.observationsTotal }}</div>
      <div v-if="location.datasheetUsage">
        <div class="font-weight-bold mt-2">Datasheet Usage</div>
        <div class="small">
          <div
            :class="{
              'font-weight-bold text-primary':
                aggregationDatasheetUsage === 'most_frequent',
            }"
          >
            <span class="text-muted">Most Frequent:</span>
            <span class="ml-1">{{
              location.datasheetUsage.most_frequent_datasheet_name || "N/A"
            }}</span>
            <span
              v-if="location.datasheetUsage.most_frequent_count"
              class="ml-1"
            >
              ({{ location.datasheetUsage.most_frequent_count }})
            </span>
          </div>
          <div
            :class="{
              'font-weight-bold text-primary':
                aggregationDatasheetUsage === 'most_recent',
            }"
          >
            <span class="text-muted">Most Recent:</span>
            <span class="ml-1">{{
              location.datasheetUsage.most_recent_datasheet_name || "N/A"
            }}</span>
          </div>
        </div>
      </div>
      <div v-else>
        <div class="small text-muted mt-2">
          No datasheet usage data for this location
        </div>
      </div>
    </div>

    <div class="mt-2">
      <LocationNavDropdown :locationId="location.id" :projectId="project.id" />
      <b-link
        class="btn btn-primary"
        :href="`https://www.google.com/maps/search/?api=1&query=${location.lngLat.lat},${location.lngLat.lng}`"
        target="_blank"
        rel="noopener noreferrer"
      >
        Directions
      </b-link>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import LocationNavDropdown from "../../../common/map/LocationNavDropdown.vue";

export default {
  name: "LocationPopupContent",

  components: {
    LocationNavDropdown,
  },

  props: {
    location: {
      type: Object,
      required: true,
    },
  },

  computed: {
    ...mapState({
      resultType: (state) => state.project.map.resultType,
      resultId: (state) => state.project.map.resultId,
      resultLabel: (state) => state.project.map.resultLabel,
      datasheetAggregation: (state) => state.project.map.datasheetAggregation,
      aggregationNumeric: (state) => state.project.map.aggregationNumeric,
      aggregationCategorical: (state) =>
        state.project.map.aggregationCategorical,
      aggregationDatasheetUsage: (state) =>
        state.project.map.aggregationDatasheetUsage,
      project: (state) => state.project.get.project,
    }),

    getLocationResult() {
      if (
        !this.resultId ||
        !this.location.results ||
        !this.location.results[this.resultId]
      ) {
        return null;
      }
      return this.location.results[this.resultId];
    },

    showLegacyDataWarning() {
      // Show warning if:
      // This location has observations
      // This datasheet has no getLocationResult or has zero observations

      if (this.location.observationsTotal === 0) return false;
      if (
        this.getLocationResult &&
        this.getLocationResult.observationsTotal ===
          this.location.observationsTotal
      )
        return false;
      if (!this.project || !this.project.createdAt) return true;
      // If project created before 2021-01-01, show warning
      // Parse the ISO date string and compare to 2021-01-01
      const createdDate = new Date(this.project.createdAt);
      const cutoffDate = new Date("2021-01-01");
      console.log("Project created at:", createdDate);
      return createdDate < cutoffDate;
    },
  },

  methods: {
    formatValue(value) {
      if (value === null || value === undefined) {
        return "N/A";
      }
      return parseFloat(value).toFixed(2);
    },
  },
};
</script>

<style scoped></style>
